import { Link } from "react-router-dom";
import { Factory, Ruler, Wrench, ShieldCheck, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLang } from "../context/LanguageContext";
import { useSiteImages } from "../hooks/use-site-images";

const GALLERY = [
  { url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&q=80", type: "work_tables" },
  { url: "https://images.unsplash.com/photo-1556909172-54557c7e4fb7?auto=format&fit=crop&w=800&q=80", type: "sinks" },
  { url: "https://images.unsplash.com/photo-1621263764928-df1444c5e859?auto=format&fit=crop&w=800&q=80", type: "shelves" },
  { url: "https://images.unsplash.com/photo-1585237017125-24baf8d7406f?auto=format&fit=crop&w=800&q=80", type: "hoods" },
  { url: "https://images.unsplash.com/photo-1556909195-27b0b96e2e7d?auto=format&fit=crop&w=800&q=80", type: "trolleys" },
  { url: "https://images.unsplash.com/photo-1591189863430-ab87e120f312?auto=format&fit=crop&w=800&q=80", type: "counters" },
  { url: "https://images.unsplash.com/photo-1556910096-6f5e72db6803?auto=format&fit=crop&w=800&q=80", type: "cabinets" },
  { url: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=800&q=80", type: "custom" },
];

export default function Fabrication() {
  const { t, isRTL } = useLang();
  const siteImage = useSiteImages();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const f = t.stainlessFactory;

  return (
    <div data-testid="fabrication-page">
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-slate-50 via-white to-cyan-50/40 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] text-[#ef4444] font-semibold mb-4 uppercase">
                <Factory className="w-3.5 h-3.5" />
                {f.eyebrow}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 text-[#0891b2]">
                {f.title}
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">{f.subtitle}</p>

              <div className="grid grid-cols-3 gap-3 mb-8">
                {[
                  { icon: Ruler, label: f.badge1 },
                  { icon: Wrench, label: f.badge2 },
                  { icon: ShieldCheck, label: f.badge3 },
                ].map((b, i) => (
                  <div key={i} className="text-center bg-white border border-slate-200 rounded-xl p-4">
                    <b.icon className="w-5 h-5 text-[#ef4444] mx-auto mb-2" />
                    <div className="text-xs font-semibold text-slate-700">{b.label}</div>
                  </div>
                ))}
              </div>

              <Link to="/quote">
                <Button data-testid="fab-cta" className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 px-6 font-semibold">
                  {f.cta} <Arrow className="w-4 h-4 ms-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src={siteImage("fabrication_hero", "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1000&q=80")}
                alt="Factory"
                className="rounded-3xl shadow-2xl w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -top-5 -end-4 bg-white rounded-xl px-5 py-4 shadow-2xl border border-slate-100">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{f.statLabel}</div>
                <div className="font-display text-2xl text-[#0891b2]">{f.statValue}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
              {t.fabricationPage.galleryEyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0891b2] mb-3 leading-tight">
              {t.fabricationPage.galleryTitle}
            </h2>
            <p className="text-slate-600">{t.fabricationPage.gallerySubtitle}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {GALLERY.map((g, i) => (
              <div
                key={i}
                data-testid={`fab-item-${i}`}
                className="group relative aspect-square overflow-hidden rounded-2xl bg-slate-100 border border-slate-200 hover:border-[#ef4444] transition-all"
              >
                <img
                  src={siteImage(`fabrication_gallery_${i + 1}`, g.url)}
                  alt={f.items[g.type]}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/85 via-[#0f172a]/20 to-transparent" />
                <div className="absolute bottom-4 start-4 end-4 text-white">
                  <div className="text-[10px] uppercase tracking-widest text-red-300 mb-1">{t.fabricationPage.customBuild}</div>
                  <div className="font-display text-base leading-snug">{f.items[g.type]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials & specs */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-6">
            {t.fabricationPage.specs.map((s, i) => (
              <div key={i} data-testid={`fab-spec-${i}`} className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="text-[10px] uppercase tracking-widest text-[#ef4444] font-semibold mb-2">{s.label}</div>
                <h3 className="font-display text-lg text-[#0891b2] mb-2">{s.title}</h3>
                <p className="text-slate-600 text-sm">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
